import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { devWarn } from "@/lib/log";

const ThemeContext = createContext(null);

const STORAGE_KEY = "rimtask.theme";
// Note: only "light"/"dark" strings are ever stored in this key — no PII/auth data.

function resolveInitial() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch (e) { devWarn("theme initial resolve failed (localStorage unavailable)", e); }
  // Premium Industrial: default to obsidian dark mode when the user hasn't
  // explicitly chosen — matches the factory-floor aesthetic and reduces eye
  // strain in low-light shop environments.
  return "dark";
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => (typeof window !== "undefined" ? resolveInitial() : "light"));

  // Apply .dark class to <html> so Tailwind darkMode: 'class' picks it up
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    // Sync mobile status-bar colour
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme === "dark" ? "#0A0A0A" : "#FF3B30");
  }, [theme]);

  // React to system-preference changes when user hasn't explicitly chosen
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => {
      if (!localStorage.getItem(STORAGE_KEY)) setTheme(e.matches ? "dark" : "light");
    };
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  const toggle = useCallback(() => {
    setTheme((t) => {
      const next = t === "dark" ? "light" : "dark";
      try { localStorage.setItem(STORAGE_KEY, next); } catch (e) { devWarn("theme persist failed", e); }
      return next;
    });
  }, []);

  const setThemeManual = useCallback((next) => {
    if (next !== "light" && next !== "dark" && next !== "auto") return;
    if (next === "auto") {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) { devWarn("theme clear failed", e); }
      setTheme(window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light");
    } else {
      try { localStorage.setItem(STORAGE_KEY, next); } catch (e) { devWarn("theme persist failed", e); }
      setTheme(next);
    }
  }, []);

  const value = useMemo(() => ({ theme, toggle, setTheme: setThemeManual }), [theme, toggle, setThemeManual]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
